import { trackEvent, type AnalyticsParams } from "@/lib/analytics";
import type {
  MiroFishInteractionMode,
  MiroFishProjectSummary,
  MiroFishSimulationPayload,
} from "@/types/mirofish";

type SourceFileLike = Pick<File, "name" | "size" | "type">;

type ProjectInput = {
  files: SourceFileLike[];
  prompt: string;
  sourceMode?: "upload" | "link";
  sourceHost?: string;
};

type SimulationStartSource = "auto_start" | "manual_start" | "manual_retry";

const round2 = (value: number) => Math.round(value * 100) / 100;

const lengthBucket = (length: number) => {
  if (length < 40) return "short";
  if (length < 180) return "medium";
  if (length < 600) return "long";
  return "very_long";
};

const promptLengthBucket = (prompt: string) => lengthBucket(prompt.trim().length);

const uiSource = (source: string): AnalyticsParams => ({
  ui_source: source,
});

const extensionForFile = (file: SourceFileLike) => {
  const name = file.name.toLowerCase();
  const match = name.match(/\.([a-z0-9]+)$/);
  if (match?.[1]) {
    return match[1].slice(0, 12);
  }
  if (file.type.includes("pdf")) return "pdf";
  if (file.type.includes("markdown")) return "md";
  if (file.type.includes("text")) return "txt";
  return "unknown";
};

const summarizeFiles = (files: SourceFileLike[]): AnalyticsParams => {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const fileTypes = Array.from(new Set(files.map(extensionForFile))).sort();

  return {
    source_document_count: files.length,
    source_document_total_mb: round2(totalBytes / 1024 / 1024),
    source_document_types: fileTypes.join(",") || "none",
  };
};

const summarizeProjectInput = ({
  files,
  prompt,
  sourceMode = "upload",
  sourceHost,
}: ProjectInput): AnalyticsParams => ({
  ...summarizeFiles(files),
  prompt_length: prompt.trim().length,
  prompt_length_bucket: promptLengthBucket(prompt),
  source_mode: sourceMode,
  ...(sourceHost ? { source_host: sourceHost } : {}),
});

const errorKind = (error: unknown) => {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const normalized = message.toLowerCase();

  if (normalized.includes("insufficient credits") || normalized.includes("402")) {
    return "insufficient_credits";
  }
  if (normalized.includes("upload") || normalized.includes("r2")) {
    return "upload_error";
  }
  if (normalized.includes("background task") || normalized.includes("queue")) {
    return "queue_unavailable";
  }
  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("network") ||
    normalized.includes("interrupted")
  ) {
    return "network_interruption";
  }
  if (normalized.includes("quota")) {
    return "quota_exhausted";
  }
  if (normalized.includes("runtime") || normalized.includes("missing python")) {
    return "runtime_unavailable";
  }
  return "unknown";
};

export function trackMiroFishSourceDocumentsSelected({
  files,
  addedCount,
  method,
}: {
  files: SourceFileLike[];
  addedCount: number;
  method: "file_picker" | "drag_drop";
}) {
  trackEvent("mirofish_source_documents_selected", {
    method,
    added_document_count: addedCount,
    ...summarizeFiles(files),
  });
}

export function trackMiroFishSourceDocumentRemoved({
  remainingFiles,
}: {
  remainingFiles: SourceFileLike[];
}) {
  trackEvent("mirofish_source_document_removed", summarizeFiles(remainingFiles));
}

export function trackMiroFishStartEngineGate({
  source,
  result,
}: {
  source: "hero_cta" | "engine_console";
  result: "allowed" | "sign_in" | "pricing" | "error";
}) {
  trackEvent("mirofish_start_engine_gate", {
    ...uiSource(source),
    result,
  });
}

export function trackMiroFishProjectCreateStart(input: ProjectInput) {
  trackEvent("mirofish_project_create_start", summarizeProjectInput(input));
}

export function trackMiroFishProjectCreateRetry({
  retry,
  maxRetries,
}: {
  retry: number;
  maxRetries: number;
}) {
  trackEvent("mirofish_project_create_retry", {
    retry,
    max_retries: maxRetries,
  });
}

export function trackMiroFishProjectCreateSuccess({
  files,
  prompt,
  sourceMode,
  sourceHost,
  recovered,
}: ProjectInput & {
  recovered?: boolean;
}) {
  const params = {
    ...summarizeProjectInput({ files, prompt, sourceMode, sourceHost }),
    recovered: Boolean(recovered),
  };

  trackEvent("generate_lead", {
    lead_source: "mirofish_engine_console",
    ...params,
  });
  trackEvent("mirofish_project_create_success", params);
}

export function trackMiroFishProjectCreateFailure({
  files,
  prompt,
  sourceMode,
  sourceHost,
  error,
}: ProjectInput & {
  error: unknown;
}) {
  trackEvent("mirofish_project_create_failure", {
    ...summarizeProjectInput({ files, prompt, sourceMode, sourceHost }),
    error_kind: errorKind(error),
  });
}

export function trackMiroFishHistoryOpen(item: MiroFishProjectSummary) {
  trackEvent("select_content", {
    content_type: "mirofish_history_project",
    project_status: item.status,
    source_document_count: item.sourceDocuments.length,
    current_round: item.currentRound,
    total_rounds: item.totalRounds,
  });
}

export function trackMiroFishGraphBuildStatus({
  status,
  hasGraph,
  error,
}: {
  status: string;
  hasGraph: boolean;
  error?: unknown;
}) {
  const params = {
    project_status: status,
    has_graph: hasGraph,
    ...(error ? { error_kind: errorKind(error) } : {}),
  };

  trackEvent("mirofish_graph_build_status", params);

  if (hasGraph) {
    trackEvent("mirofish_graph_build_success", {
      project_status: status,
      has_graph: hasGraph,
    });
    return;
  }

  if (error || status.toLowerCase() === "failed") {
    trackEvent("mirofish_graph_build_failure", params);
  }
}

export function trackMiroFishSimulationStartRequested({
  source,
  status,
}: {
  source: SimulationStartSource;
  status?: MiroFishSimulationPayload["status"];
}) {
  trackEvent("mirofish_simulation_start_requested", {
    ...uiSource(source),
    simulation_status: status || "unknown",
  });
}

export function trackMiroFishSimulationStartSuccess({
  source,
  status,
}: {
  source: SimulationStartSource;
  status?: MiroFishSimulationPayload["status"];
}) {
  trackEvent("mirofish_simulation_start_success", {
    ...uiSource(source),
    simulation_status: status || "unknown",
  });
}

export function trackMiroFishSimulationStartFailure({
  source,
  error,
}: {
  source: SimulationStartSource;
  error: unknown;
}) {
  trackEvent("mirofish_simulation_start_failure", {
    ...uiSource(source),
    error_kind: errorKind(error),
  });
}

export function trackMiroFishReportOpen({
  source,
  status,
}: {
  source: "simulation_stage" | "report_stage";
  status?: string;
}) {
  const params = {
    content_type: "mirofish_report",
    ...uiSource(source),
    project_status: status || "unknown",
  };

  trackEvent("select_content", params);
  trackEvent("mirofish_report_open", params);
}

export function trackMiroFishReportRetry({
  result,
  error,
}: {
  result: "requested" | "success" | "failure";
  error?: unknown;
}) {
  trackEvent("mirofish_report_retry", {
    result,
    ...(error ? { error_kind: errorKind(error) } : {}),
  });
}

export function trackMiroFishInteractionOpen({
  status,
}: {
  status?: string;
}) {
  const params = {
    content_type: "mirofish_interaction",
    project_status: status || "unknown",
  };

  trackEvent("select_content", params);
  trackEvent("mirofish_interaction_open", params);
}

export function trackMiroFishInteractionModeChange({
  mode,
}: {
  mode: MiroFishInteractionMode;
}) {
  trackEvent("mirofish_interaction_mode_change", {
    interaction_mode: mode,
  });
}

export function trackMiroFishInteractionMessage({
  mode,
  messageLength,
  result,
  error,
}: {
  mode: MiroFishInteractionMode;
  messageLength: number;
  result: "requested" | "success" | "failure";
  error?: unknown;
}) {
  trackEvent("mirofish_interaction_message", {
    interaction_mode: mode,
    message_length: messageLength,
    message_length_bucket: lengthBucket(messageLength),
    result,
    ...(error ? { error_kind: errorKind(error) } : {}),
  });
}
